import { getCMSViewLimit } from '@/components/CollectionTable/utils';
import { StyledDialogOverlay } from '@/components/Dialog';
import { Note, X } from '@phosphor-icons/react';
import * as Dialog from '@radix-ui/react-dialog';
import { SUBSCRIPTION_PLAN } from '@typedream/data';
import Link from 'next/link';
import React, { useContext, useMemo } from 'react';
import { useSpaceAndPageIDFromRouter } from '../../../../data/space/hooks';
import { useGetSpaceQuery } from '../../../../store';
import { CollectionPaywallContext } from '../../context/CollectionPaywallContextProvider';
import {
  CollectionSettingsOpenContext,
  CollectionSettingsOpenDispatchContext,
} from '../../context/CollectionSettingsContextProvider/openContext';
import ConnectData from './ConnectData';
import ConnectTable from './ConnectTable';
import CustomizeTemplate from './CustomizeTemplate';
import DataSetup from './DataSetup';
import Fields from './Fields';
import Search from './Search';
import Sidebar from './Sidebar';
import SortFilter from './SortFilter';
import Template from './Template';
import { SETTINGS_PAGE } from './constants';
import {
  CollectionSettingsDataContext,
  CollectionSettingsPageContext,
  CollectionSettingsPopupProvider,
} from './contextProvider';

const completeSettingPages = {
  [SETTINGS_PAGE.DATA_SETUP]: DataSetup,
  [SETTINGS_PAGE.CONNECT_DATA]: ConnectData,
  [SETTINGS_PAGE.CONNECT_TABLE]: ConnectTable,
  [SETTINGS_PAGE.SORT_FILTER]: SortFilter,
  [SETTINGS_PAGE.SEARCH]: Search,
  [SETTINGS_PAGE.FIELDS]: Fields,
  [SETTINGS_PAGE.TEMPLATE]: Template,
  [SETTINGS_PAGE.CUSTOMIZE_TEMPLATE]: CustomizeTemplate,
};

const settingPages = {
  [SETTINGS_PAGE.DATA_SETUP]: DataSetup,
  [SETTINGS_PAGE.CONNECT_DATA]: ConnectData,
  [SETTINGS_PAGE.CONNECT_TABLE]: ConnectTable,
};

function CollectionSettingsBase({
  plan = SUBSCRIPTION_PLAN.FREE,
  total,
}: {
  total?: number;
  plan?: SUBSCRIPTION_PLAN;
}) {
  const settingsOpen = useContext(CollectionSettingsOpenContext);
  const setsettingsOpen = useContext(CollectionSettingsOpenDispatchContext);
  const settingsPage = useContext(CollectionSettingsPageContext);
  const [spaceID] = useSpaceAndPageIDFromRouter();
  const { data: space } = useGetSpaceQuery([spaceID]);
  const { upgradeRequired } = useContext(CollectionSettingsDataContext);
  const { limit } = useContext(CollectionPaywallContext);

  const SettingPage =
    (upgradeRequired
      ? settingPages[settingsPage as SETTINGS_PAGE]
      : completeSettingPages[settingsPage as SETTINGS_PAGE]) || React.Fragment;

  const freeOrOldLaunch = useMemo(
    () => [SUBSCRIPTION_PLAN.FREE, SUBSCRIPTION_PLAN.LAUNCH].includes(plan),
    [plan]
  );

  const message = useMemo(() => {
    if (freeOrOldLaunch) {
      const newLaunchLimit = getCMSViewLimit(SUBSCRIPTION_PLAN.LAUNCH_15_20).limit as number;

      if (total === undefined) {
        return (
          <>
            Only {limit} item{limit !== 1 ? 's' : ''} will be displayed in published version -
            <Link
              href={`/settings/${space?.slug}/${spaceID}/billing`}
              className="underline text-accent hover:text-indigo-500"
            >
              Upgrade to the new Launch Plan to display up to {newLaunchLimit} collection item
              {newLaunchLimit !== 1 ? 's' : ''}!
            </Link>
          </>
        );
      }

      if (total > newLaunchLimit) {
        return (
          <>
            Only {limit} item{limit !== 1 ? 's' : ''} will be displayed in published version -
            <Link
              href={`/settings/${space?.slug}/${spaceID}/billing`}
              className="underline text-accent hover:text-indigo-500"
            >
              Upgrade to Grow Plan to display more than {newLaunchLimit} collection item
              {newLaunchLimit !== 1 ? 's' : ''}!
            </Link>
          </>
        );
      }
      return (
        <>
          Only {limit} item{limit !== 1 ? 's' : ''} will be displayed in published version -
          <Link
            href={`/settings/${space?.slug}/${spaceID}/billing`}
            className="underline text-accent hover:text-indigo-500"
          >
            Upgrade to Launch plan to display up to {newLaunchLimit} collection item
            {newLaunchLimit !== 1 ? 's' : ''}!
          </Link>
        </>
      );
    }

    if (plan === SUBSCRIPTION_PLAN.LAUNCH_15_20) {
      if (total !== undefined && total > (limit || 1000)) {
        return (
          <>
            Only up to {limit} item{limit !== 1 ? 's' : ''} will be displayed in published version -
            <Link
              href={`/settings/${space?.slug}/${spaceID}/billing`}
              className="underline text-accent hover:text-indigo-500"
            >
              Upgrade to Grow Plan to display more than {limit} collection item
              {limit !== 1 ? 's' : ''}!
            </Link>
          </>
        );
      }
    }

    return null;
  }, [plan, total, space, spaceID, freeOrOldLaunch]);

  const showBanner = useMemo(() => {
    if (freeOrOldLaunch || (plan === SUBSCRIPTION_PLAN.LAUNCH_15_20 && limit && total > limit)) {
      return true;
    }
    return false;
  }, [plan, total, freeOrOldLaunch]);

  return (
    <Dialog.Root open={settingsOpen} onOpenChange={setsettingsOpen}>
      <Dialog.Overlay asChild>
        <StyledDialogOverlay />
      </Dialog.Overlay>
      <Dialog.Portal>
        <Dialog.Content
          className="fixed top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2 flex max-w-[85vw] max-h-[85vh] bg-background overflow-hidden outline-none rounded-2xl z-10"
          style={{
            width: 'min(1400px, 90vw)',
            height: 'min(800px, 90vh)',
          }}
        >
          <Sidebar />
          <div className="flex-1 h-full" style={{ width: 'calc(100% - 120px)' }}>
            <div className="flex flex-col w-full h-full overflow-hidden">
              {showBanner ? (
                <div className="flex items-center justify-center py-4 px-4 bg-[var(--primary-25)]">
                  <div className="flex items-center justify-center gap-2.5">
                    <Note className="text-accent" size={20} weight="fill" />
                    {message}
                  </div>
                </div>
              ) : null}
              <div className="w-full flex-1 overflow-hidden">
                <div className="w-full h-full p-7 overflow-auto">
                  <SettingPage />
                </div>
              </div>
            </div>
          </div>
          <Dialog.Close
            className="absolute flex bg-background-tertiary border-0 rounded-full p-1 cursor-pointer"
            style={{ top: 20, right: 20 }}
            aria-label="Close"
          >
            <X size={16} className="text-secondary" />
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
function CollectionSettings(props: { total?: number; plan?: SUBSCRIPTION_PLAN }) {
  return (
    <CollectionSettingsPopupProvider>
      <CollectionSettingsBase {...props} />
    </CollectionSettingsPopupProvider>
  );
}
export default CollectionSettings;
